import React, { useState, useEffect } from 'react';
import {
  FileCode, Plus, Trash2, Edit, Play, Download, Upload,
  X, Save, AlertCircle, CheckCircle, Loader2, Wrench,
  Wifi, Printer, Search, FolderOpen, Send
} from 'lucide-react';
import api from '../services/api';

const CATEGORIES = [
  { id: 'general', name: 'General', icon: FileCode },
  { id: 'reparacion', name: 'Reparacion', icon: Wrench },
  { id: 'red', name: 'Red/Conexion', icon: Wifi },
  { id: 'impresora', name: 'Impresora', icon: Printer },
  { id: 'diagnostico', name: 'Diagnostico', icon: Search }
];

export default function ScriptsManager({ onClose, onSelectScript }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScript, setSelectedScript] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('all');

  // Form state para crear/editar
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'general',
    requiresAdmin: false,
    content: '@echo off\nREM Script de reparacion\n\necho Ejecutando...\npause'
  });

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/scripts');
      setScripts(response.data.scripts || []);
    } catch (error) {
      console.error('Error cargando scripts:', error);
      showMessage('error', 'Error al cargar scripts');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      showMessage('error', 'Nombre y contenido son requeridos');
      return;
    }

    try {
      const response = await api.post('/api/scripts/create', formData);
      setScripts([...scripts, response.data.script]);
      showMessage('success', 'Script creado correctamente');
      setCreateMode(false);
      resetForm();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al crear script');
    }
  };

  const handleUpdate = async () => {
    if (!selectedScript) return;

    try {
      const response = await api.put(`/api/scripts/${selectedScript.id}`, formData);
      setScripts(scripts.map(s => s.id === selectedScript.id ? response.data.script : s));
      showMessage('success', 'Script actualizado');
      setEditMode(false);
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al actualizar');
    }
  };

  const handleDelete = async (scriptId) => {
    if (!window.confirm('¿Eliminar este script?')) return;

    try {
      await api.delete(`/api/scripts/${scriptId}`);
      setScripts(scripts.filter(s => s.id !== scriptId));
      if (selectedScript?.id === scriptId) {
        setSelectedScript(null);
      }
      showMessage('success', 'Script eliminado');
    } catch (error) {
      showMessage('error', 'Error al eliminar script');
    }
  };

  const handleViewScript = async (script) => {
    try {
      const response = await api.get(`/api/scripts/${script.id}`);
      setSelectedScript(response.data.script);
      setFormData({
        name: response.data.script.name,
        description: response.data.script.description || '',
        category: response.data.script.category || 'general',
        requiresAdmin: response.data.script.requiresAdmin || false,
        content: response.data.script.content || ''
      });
    } catch (error) {
      showMessage('error', 'Error al cargar script');
    }
  };

  const handleSelectForChat = (script) => {
    if (onSelectScript) {
      onSelectScript(script);
      if (onClose) onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'general',
      requiresAdmin: false,
      content: '@echo off\nREM Script de reparacion\n\necho Ejecutando...\npause'
    });
    setSelectedScript(null);
    setEditMode(false);
  };

  const filteredScripts = filter === 'all'
    ? scripts
    : scripts.filter(s => s.category === filter);

  const getCategoryIcon = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.icon : FileCode;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <FileCode className="w-6 h-6" />
            <div>
              <h2 className="font-bold text-lg">Scripts de Reparacion</h2>
              <p className="text-sm text-white/70">Gestiona los BATs para las taquillas</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center gap-2 ${
            message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            {message.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Lista de scripts */}
          <div className="w-1/3 border-r flex flex-col">
            {/* Filtros y acciones */}
            <div className="p-3 border-b space-y-2">
              <button
                onClick={() => { setCreateMode(true); resetForm(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4" />
                Nuevo Script
              </button>

              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">Todas las categorias</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredScripts.length === 0 ? (
                <div className="text-center p-6 text-gray-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay scripts</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredScripts.map(script => {
                    const Icon = getCategoryIcon(script.category);
                    return (
                      <div
                        key={script.id}
                        onClick={() => handleViewScript(script)}
                        className={`p-3 cursor-pointer hover:bg-gray-50 transition ${
                          selectedScript?.id === script.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            script.requiresAdmin ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{script.name}</h4>
                            <p className="text-xs text-gray-500 truncate">{script.description || 'Sin descripcion'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">
                                {CATEGORIES.find(c => c.id === script.category)?.name || 'General'}
                              </span>
                              {script.requiresAdmin && (
                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">
                                  Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Panel de detalle/edicion */}
          <div className="flex-1 flex flex-col">
            {createMode || editMode ? (
              /* Formulario de crear/editar */
              <div className="flex-1 p-4 overflow-y-auto">
                <h3 className="font-medium mb-4">
                  {createMode ? 'Crear nuevo script' : 'Editar script'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ej: Reparar conexion red"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Descripcion</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Descripcion del script"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Categoria</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.requiresAdmin}
                          onChange={(e) => setFormData({ ...formData, requiresAdmin: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Requiere Admin</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Contenido del script</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                      rows={12}
                      placeholder="@echo off&#10;REM Tu script aqui"
                    />
                  </div>
                </div>

                {/* Botones */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={createMode ? handleCreate : handleUpdate}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4" />
                    {createMode ? 'Crear' : 'Guardar'}
                  </button>
                  <button
                    onClick={() => { setCreateMode(false); setEditMode(false); resetForm(); }}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : selectedScript ? (
              /* Vista de script seleccionado */
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{selectedScript.name}</h3>
                      <p className="text-sm text-gray-500">{selectedScript.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditMode(true)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(selectedScript.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                      {CATEGORIES.find(c => c.id === selectedScript.category)?.name}
                    </span>
                    {selectedScript.requiresAdmin && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded">
                        Requiere Admin
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      Subido por: {selectedScript.uploadedByName}
                    </span>
                  </div>
                </div>

                {/* Contenido del script */}
                <div className="flex-1 p-4 overflow-auto">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono whitespace-pre-wrap">
                    {selectedScript.content}
                  </pre>
                </div>

                {/* Acciones */}
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSelectForChat(selectedScript)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <Send className="w-4 h-4" />
                      Usar en Chat
                    </button>
                    <a
                      href={`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://chatmanantial.com'}/api/scripts/${selectedScript.id}/download`}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                      download
                    >
                      <Download className="w-4 h-4" />
                      Descargar
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              /* Estado vacio */
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FileCode className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Selecciona un script para ver detalles</p>
                  <p className="text-sm">o crea uno nuevo</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
